/* eslint-disable react/display-name */
import React, { memo } from 'react';
import { isEmpty } from 'lodash-es';
import { extractAndValidateJson } from '../../utils';
import { ChartCmp } from './index';
import { formatDataSourceWithBiDataInfo, getDataFilterKey } from './_utils';

/**
 * Markdown 节点属性接口
 */
interface IMarkdownNode {
  tagName: string;
  [key: string]: any;
}

/**
 * Rest Props 接口
 */
interface IRestProps {
  node?: IMarkdownNode;
  children?: string;
  [key: string]: any;
}

/**
 * 答案任务项接口
 */
interface IAnswerTask {
  type: string;
  value?: IBiDataItem[];
}

/**
 * BI 数据项接口
 */
interface IBiDataItem {
  data_id: string;
  title?: string;
  data?: {
    title?: string;
    [key: string]: any;
  };
  from?: string;
  chartHeight?: number | string;
  [key: string]: any;
}

/**
 * 数据图表渲染组件 Props 接口
 */
interface IDataChartRendererProps {
  /** Markdown 解析后的节点属性 */
  restProps: IRestProps;
  /** 目标标签名称（databraindata 或 dbd） */
  tagName: string;
  /** 答案数据数组 */
  answer: IAnswerTask[];
  /** 数据来源标识 */
  from: string;
  /** 图表高度 */
  chartHeight?: number | string;
  /** 已解析的 data_id 集合引用，用于流式输出去重 */
  parsedDataIdsRef: React.MutableRefObject<Set<string>>;
}

/**
 * 数据图表渲染组件（可复用）
 * 
 * 用于处理 Markdown 中的 databraindata 和 dbd 标签，将其渲染为图表组件。
 * 支持流式输出场景下的去重机制，避免相同 data_id 的图表重复渲染。
 * 
 * @param props - 组件属性
 * @returns 图表组件或 null
 */
export const DataChartRenderer = memo(
  (props: IDataChartRendererProps) => {
    const { restProps, tagName, answer, from, chartHeight, parsedDataIdsRef } = props;

    // 1、判断是否是databraindata占位符，如果不是则提前返回null，符合规范
    if (restProps?.node?.tagName !== tagName) {
      return null;
    }
    const dataBrainBiDataInfo = extractAndValidateJson(restProps?.children) || {};
    // dbd 的 chart_type 为字符串 table 时转成数组，与下游约定对齐
    if (dataBrainBiDataInfo.chart_type === 'table') {
      dataBrainBiDataInfo.chart_type = ['table'];
    }

    const { data_id, title, chat_type, chart_type, filter_info, filter_default_value, format } = dataBrainBiDataInfo || {};
    // 3、再次兜底,如果没有data_id，则返回null
    if (isEmpty(answer) || !data_id) return null;
    // 4、检查该 data_id 是否已经解析过，如果是则返回 null（流式输出去重）
    // filter_info和data_id都一样，才不展示后面的图
    const dataFilterKey = getDataFilterKey(dataBrainBiDataInfo as any);
    if (parsedDataIdsRef.current.has(dataFilterKey)) {
      return null;
    }
    // 5、提取数据
    const answerBiData = !isEmpty(answer) && answer
      ?.filter((task: { type: string; }) => task?.type === 'biData')
      .flatMap((item: any) => item.value);
    const dataSource = answerBiData?.find(
      (item: { data_id: string; }) => item.data_id === data_id,
    );
    const finalDataSource = formatDataSourceWithBiDataInfo(
      dataSource,
      { chat_type, chart_type, filter_info, filter_default_value, title, format },
      { from, chartHeight }
    );
   
    // 6、如果dataSource为空，则返回null、不展示图表
    if (isEmpty(finalDataSource)) return null;
    // 6、格式化 dataSource，优先使用 dataBrainBiDataInfo 中的配置（仅 v2 格式）
    // 7、标记该 data_id 已解析，后续相同 data_id 不再解析
    parsedDataIdsRef.current.add(dataFilterKey);
   
    return (
      <div>
        <ChartCmp value={[finalDataSource]} />
      </div>
    );
  },
  (prevProps: IDataChartRendererProps, nextProps: IDataChartRendererProps) => {
    // 自定义比较函数：仅当 data_id 相同时才跳过更新
    const getDataId = (props: IDataChartRendererProps): string | undefined =>
      extractAndValidateJson(props.restProps?.children)?.data_id;

    const prevDataId = getDataId(prevProps);
    const nextDataId = getDataId(nextProps);

    // 返回 true 表示 props 相等，跳过更新
    return prevDataId === nextDataId;
  },
);
